import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Phone, MapPin, Printer, Share2, FileText, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LoadingState from '@/components/shared/LoadingState';
import ErrorState from '@/components/shared/ErrorState';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';

function fmt(n) { return Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

const METHOD_ICON = { cash: '💵', bank: '🏦', check: '📝', wallet: '📱', other: '💳' };
const METHOD_LABEL = { cash: 'نقدي', bank: 'تحويل بنكي', check: 'شيك', wallet: 'محفظة', other: 'أخرى' };

function getMethodKey(pmt) {
  const jn = Array.isArray(pmt.journal_id) ? pmt.journal_id[1] : (pmt.journal_id || '');
  if (jn.toLowerCase().includes('cash') || jn.includes('نقد') || jn.includes('صندوق')) return 'cash';
  if (jn.toLowerCase().includes('bank') || jn.includes('بنك')) return 'bank';
  return 'other';
}

export default function CollectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.get(`/collections/payments/${parseInt(id)}`);
    if (res?.success) setPayment(res.data);
    else setError(res?.error || 'فشل تحميل التحصيل');
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleShare = () => {
    if (!payment) return;
    const partner = payment.partner_details;
    const text = `إيصال تحصيل: ${payment.name}\nالعميل: ${partner?.name || ''}\nالمبلغ: ${fmt(payment.amount)} ${cur()}\nالتاريخ: ${payment.date}`;
    if (navigator.share) navigator.share({ title: payment.name, text });
    else { navigator.clipboard.writeText(text); }
  };

  if (loading) return <div className="p-4"><LoadingState message="جاري تحميل التحصيل..." /></div>;
  if (error) return <div className="p-4"><ErrorState message={error} onRetry={load} /></div>;
  if (!payment) return null;

  const partner = payment.partner_details;
  const customerName = partner?.name || (Array.isArray(payment.partner_id) ? payment.partner_id[1] : '—');
  const methodKey = getMethodKey(payment);
  const dateStr = payment.date ? new Date(payment.date).toLocaleDateString('ar-EG', { year: 'numeric', month: 'long', day: 'numeric' }) : '—';
  const invoices = payment.invoices || [];

  return (
    <div className="flex flex-col min-h-full pb-24" dir="rtl">
      {/* Hero */}
      <div className="bg-gradient-to-br from-secondary to-emerald-600 px-5 pt-5 pb-8 text-white">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-sm font-semibold opacity-80">{payment.name}</h1>
          <span className="text-xs bg-white/20 rounded-full px-3 py-1 font-semibold flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> مكتمل
          </span>
        </div>
        <p className="text-4xl font-black tracking-tight mb-1">{fmt(payment.amount)} {cur()}</p>
        <p className="text-sm opacity-80">{dateStr}</p>
      </div>

      <div className="p-4 space-y-4 -mt-4">
        {/* Customer card */}
        <div className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4 shadow-sm">
          <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-secondary font-bold text-xl">{customerName.charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-foreground">{customerName}</p>
            {partner?.phone && (
              <div className="flex items-center gap-1 mt-0.5">
                <Phone className="w-3 h-3 text-muted-foreground" />
                <a href={`tel:${partner.phone}`} className="text-xs text-muted-foreground">{partner.phone}</a>
              </div>
            )}
            {partner?.city && (
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3 text-muted-foreground" />
                <p className="text-xs text-muted-foreground">{partner.city}</p>
              </div>
            )}
          </div>
        </div>

        {/* Payment details */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="px-4 py-3 bg-muted/30 border-b border-border">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">تفاصيل الدفع</p>
          </div>
          <div className="divide-y divide-border">
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-muted-foreground">طريقة الدفع</span>
              <span className="font-semibold text-foreground">{METHOD_ICON[methodKey]} {METHOD_LABEL[methodKey]}</span>
            </div>
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-muted-foreground">دفتر اليومية</span>
              <span className="font-semibold text-foreground">{Array.isArray(payment.journal_id) ? payment.journal_id[1] : payment.journal_id}</span>
            </div>
            {payment.ref && (
              <div className="flex justify-between px-4 py-3">
                <span className="text-sm text-muted-foreground">المرجع</span>
                <span className="font-semibold text-foreground">{payment.ref}</span>
              </div>
            )}
            <div className="flex justify-between px-4 py-3">
              <span className="text-sm text-muted-foreground">المبلغ</span>
              <span className="font-black text-secondary text-lg">{fmt(payment.amount)} {cur()}</span>
            </div>
          </div>
        </div>

        {/* Invoices covered */}
        {invoices.length > 0 && (
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="px-4 py-3 bg-muted/30 border-b border-border">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">الفواتير المشمولة</p>
            </div>
            <div className="divide-y divide-border">
              {invoices.map(inv => (
                <div key={inv.id} className="flex items-center gap-3 px-4 py-3">
                  <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{inv.name}</p>
                    <p className="text-xs text-muted-foreground">{inv.invoice_date}</p>
                  </div>
                  <p className="font-bold text-primary text-sm">{fmt(inv.amount_total)} {cur()}</p>
                </div>
              ))}
              <div className="flex justify-between px-4 py-3 bg-muted/30">
                <span className="font-bold text-foreground">الإجمالي</span>
                <span className="font-black text-secondary">{fmt(payment.amount)} {cur()}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Action bar */}
      <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border px-4 py-3 flex gap-2 z-20">
        <Button onClick={() => window.print()} variant="outline" className="flex-1 rounded-xl h-11 gap-1.5">
          <Printer className="w-4 h-4" /> طباعة
        </Button>
        <Button onClick={handleShare} variant="outline" className="flex-1 rounded-xl h-11 gap-1.5">
          <Share2 className="w-4 h-4" /> مشاركة
        </Button>
        <Button onClick={() => navigate(`/collections/new?customer_id=${Array.isArray(payment.partner_id) ? payment.partner_id[0] : ''}&customer_name=${encodeURIComponent(customerName)}`)}
          className="flex-1 bg-secondary rounded-xl h-11 gap-1.5 text-sm font-bold">
          + تحصيل آخر
        </Button>
      </div>
    </div>
  );
}